import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import './Navbar.css';

/**
 * Navbar Component
 * Fixed navigation bar at the top of all pages
 * Features:
 * - Logo/brand name on the left
 * - Navigation links on the right
 * - Active link highlighting
 * - Smooth hover effects
 * - Mobile responsive with hamburger menu
 * - React Router integration
 */
function Navbar() {
  // State for mobile menu toggle
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // State for navbar shadow on scroll
  const [isScrolled, setIsScrolled] = useState(false);

  /**
   * Toggle mobile menu open/closed
   */
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  /**
   * Close mobile menu when a link is clicked
   */
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  /**
   * Add shadow to navbar on scroll
   */
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /**
   * Prevent body scroll when mobile menu is open
   */
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        {/* Logo/Brand Section */}
        <div className="navbar-brand">
          <NavLink to="/" className="navbar-logo" onClick={closeMenu}>
            {/* Small Locker Icon */}
            <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="8" y="14" width="14" height="11" rx="1.5" fill="#27AE60" />
              <path d="M 10 14 L 10 10 Q 10 6, 15 6 Q 20 6, 20 10 L 20 14" stroke="#F1C40F" strokeWidth="2" fill="none" strokeLinecap="round" />
              <circle cx="15" cy="19.5" r="1.5" fill="#1A1F36" />
            </svg>
            <span className="navbar-logo-text">lockitup</span>
          </NavLink>
        </div>

        {/* Hamburger Menu Button (Mobile) */}
        <button 
          className={`navbar-hamburger ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle navigation menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Navigation Links */}
        <div className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
          <ul className="navbar-nav">
            <li className="nav-item">
              <NavLink 
                to="/" 
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMenu}
              >
                Home
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/about" 
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMenu}
              >
                About
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/contact" 
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMenu}
              >
                Contact
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/privacy" 
                className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}
                onClick={closeMenu}
              >
                Privacy Policy
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink 
                to="/download" 
                className={({ isActive }) => isActive ? 'nav-link active nav-link-download' : 'nav-link nav-link-download'}
                onClick={closeMenu}
              >
                Download
              </NavLink>
            </li>
          </ul>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <div className="navbar-overlay" onClick={closeMenu}></div>
        )}
      </div>
    </nav>
  );
}

export default Navbar;
